import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import LoadError from 'core/components/LoadError';
import {
  useNotify,
} from 'core/hooks';
import type {
  PatientAuthorizationResponseDTO,
} from 'dtos';
import {
  useGetPatientAuthorization,
  useSavePatientAuthorization,
} from 'views/EMR/components/Authorizations/hooks';
import AuthorizationForm from 'views/EMR/components/Authorizations/Form';
import {
  AuthorizationFormValues,
  getAuthorizationFormDefaults,
  serializeAuthorization,
} from 'views/EMR/components/Authorizations/formUtils';
import AuthorizationStatusBadge from 'views/EMR/components/Authorizations/StatusBadge';
import type {
  PatientForm,
} from 'pages/Dashboard/types/patient';

type Props = {
  patient: PatientForm;
  authorizationId?: number | null;
  open: boolean;
  initialAuthorizationNumber?: string;
  initialInsuranceId?: number;
  onSaved?: (saved: PatientAuthorizationResponseDTO) => void;
  onClose: () => void;
};

type AuthorizationContentProps = {
  patient: PatientForm;
  disabled: boolean;
  isNew: boolean;
  isAuthorizationError: boolean;
  isLoadingAuthorization: boolean;
  isAuthorizationReady: boolean;
  isFetchingAuthorization: boolean;
  authorizationError: Error | null;
  refetchAuthorization: () => void;
  onClose: () => void;
};

function AuthorizationContent({
  patient,
  disabled,
  isNew,
  isAuthorizationError,
  isLoadingAuthorization,
  isAuthorizationReady,
  isFetchingAuthorization,
  authorizationError,
  refetchAuthorization,
  onClose,
}: AuthorizationContentProps) {
  const showError = !isNew && isAuthorizationError;
  const showLoading = !isNew && !showError && (isLoadingAuthorization || !isAuthorizationReady);
  const showForm = !showError && !showLoading;

  return (
    <>
      {showError && (
        <LoadError
          title="Unable to load authorization"
          message={authorizationError?.message}
          isRetrying={isFetchingAuthorization}
          onRetry={refetchAuthorization}
          onGoBack={onClose}
          classes={{ root: 'min-h-32' }}
        />
      )}
      {showLoading && (
        <div className="flex min-h-32 items-center justify-center text-sm text-gray-500">
          Loading authorization...
        </div>
      )}
      {showForm && (
        <div className="overflow-y-auto px-1">
          <AuthorizationForm patient={patient} disabled={disabled} />
        </div>
      )}
    </>
  );
}

type AuthorizationDialogTitleProps = {
  status?: PatientAuthorizationResponseDTO['status'];
  isNew: boolean;
};

function AuthorizationDialogTitle({
  status,
  isNew,
}: AuthorizationDialogTitleProps) {
  return (
    <div className="flex items-center gap-2 text-lg font-semibold">
      {status ? 'Authorization' : `${isNew ? 'Add' : 'Edit'} Authorization`}
      {status && <AuthorizationStatusBadge status={status} />}
    </div>
  );
}

export default function AuthorizationDialog({
  patient,
  authorizationId,
  open,
  initialAuthorizationNumber,
  initialInsuranceId,
  onSaved,
  onClose,
}: Props) {
  const isNew = !(authorizationId && authorizationId > 0);
  const {
    data: authorization,
    isLoading: isLoadingAuthorization,
    isError: isAuthorizationError,
    error: authorizationError,
    isFetching: isFetchingAuthorization,
    refetch: refetchAuthorization,
  } = useGetPatientAuthorization(authorizationId ?? 0, { enabled: open && !isNew });
  const { mutateAsync: saveAuthorization, isPending } = useSavePatientAuthorization();
  const notify = useNotify();
  const defaultValues = useMemo(
    () => getAuthorizationFormDefaults(
      isNew ? undefined : authorization,
      isNew
        ? {
          authorizationNumber: initialAuthorizationNumber ?? '',
          insuranceId: initialInsuranceId,
        }
        : undefined,
    ),
    [authorization, initialAuthorizationNumber, initialInsuranceId, isNew],
  );
  const formProps = useForm<AuthorizationFormValues>({
    defaultValues,
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = formProps;

  useEffect(() => {
    if (open && (isNew || authorization)) {
      reset(defaultValues);
    }
  }, [open, isNew, authorization, defaultValues, reset]);

  const onSave = async (values: AuthorizationFormValues) => {
    try {
      const saved = await saveAuthorization(serializeAuthorization(values));
      onSaved?.(saved);
      notify({
        variant: 'success',
        message: `Authorization ${isNew ? 'added' : 'updated'} successfully`,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save authorization', error);
    }
  };

  const status = authorization?.status;
  const isAuthorizationReady = isNew
    || (!isLoadingAuthorization && !isAuthorizationError && Boolean(authorization));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={<AuthorizationDialogTitle status={status} isNew={isNew} />}
      className="min-w-[38rem] max-w-[46rem]"
      disableBackdropClick
      disableEscapeKeyDown={isSubmitting || isPending}
    >
      <FormProvider {...formProps}>
        <div className="flex max-h-[75vh] min-h-0 flex-col gap-4">
          <AuthorizationContent
            patient={patient}
            disabled={isSubmitting || isPending}
            isNew={isNew}
            isAuthorizationError={isAuthorizationError}
            isLoadingAuthorization={isLoadingAuthorization}
            isAuthorizationReady={isAuthorizationReady}
            isFetchingAuthorization={isFetchingAuthorization}
            authorizationError={authorizationError}
            refetchAuthorization={refetchAuthorization}
            onClose={onClose}
          />
          <div className="flex shrink-0 flex-row-reverse justify-between gap-3 pt-4">
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || isPending || !isAuthorizationReady}
              onClick={handleSubmit(onSave)}
            >
              {getSubmitButtonText(isNew, isSubmitting || isPending)}
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
